import { IFieldChange, ProductHistoryEventType } from "@/common/types";

/**
 * MED-653 — presentation helpers for the approval-history panel. The counts and
 * the headline are composed by the backend; only timestamps and raw field
 * values are formatted here.
 *
 * MED-682: the event date and the changed-value formatters moved to
 * `@/common/utils/product-history`, because the manufacturer's Edit History
 * panel renders the same rows and the two must not drift. Re-exported so this
 * module remains the one import site for anything in this folder.
 */
export {
  formatEventDate,
  formatChangeValue,
} from "@/common/utils/product-history";

export function formatShortDate(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-GB");
}

/**
 * MED-678 — do the cumulative changes since the last approved version say
 * anything the single edit does not?
 *
 * For the common case (one edit since the approval) the two lists are identical,
 * and showing the same three rows twice under two headings is noise. The
 * comparison block earns its space only when edits have composed.
 */
export function sameChanges(a: IFieldChange[], b: IFieldChange[]) {
  if (a.length !== b.length) return false;

  const key = (change: IFieldChange) =>
    `${change.field}|${String(change.from)}|${String(change.to)}`;
  const bKeys = new Set(b.map(key));

  return a.every((change) => bKeys.has(key(change)));
}

/** Timeline dot/label colour per event type, keyed to the theme's text palette. */
export const EVENT_COLOR_KEY: Record<ProductHistoryEventType, string> = {
  submitted: "progress",
  resubmitted: "pending",
  approved: "life",
  declined: "danger",
  queried: "mild_danger",
  inventory_purged: "prim_100",
};

export function eventColorKey(eventType: ProductHistoryEventType) {
  return EVENT_COLOR_KEY[eventType] ?? "prim_100";
}
